import { makeStyles } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { ApolloError } from 'apollo-client';
import { subMinutes } from 'date-fns';
import parseISO from 'date-fns/parseISO';
import { addMiddleware, isStateTreeNode } from 'mobx-state-tree';
import React, { useEffect } from 'react';
import { KeyedMutator } from 'swr';

import { CL_CAN_ACCEPT_PRESCRIPTION_TASKS } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import {
  EventCard,
  EventCardClassification,
  Staff_GetEventCardsQuery as staffGetEventCardsQuery,
} from 'src/generated/gql/graphql';
import { EmptyFocusCard } from 'src/myDayToday/components/EmptyFocusCard';
import NonPatientEventCard from 'src/myDayToday/components/NonPatientEventCard';
import PopInVisitCard from 'src/myDayToday/components/PopInVisitCard';
import PrescriptionTaskCard from 'src/myDayToday/components/PrescriptionTaskCard';
import ScheduledEventCard from 'src/myDayToday/components/ScheduledEventCard';
import UnsignedVisitsCard from 'src/myDayToday/components/UnsignedVisitsCard';
import {
  EventTiming,
  getScheduledEventTiming,
} from 'src/myDayToday/components/getScheduledEventTiming';
import { shouldRenderEventCard } from 'src/myDayToday/domain/shouldRenderEventCard';
import { transformPatient } from 'src/myDayToday/domain/transformPatient';
import Colors from 'src/nightingale/Colors';
import type { Provider } from 'src/shared/stores/resource';
import useRootStore from 'src/stores/useRootStore';
import { ProviderRole } from 'src/stores/users/userType';
import { BoulderColors } from 'src/util/brand';
import { colors } from 'src/util/colors';

interface MainFocusAreaProps {
  currentTime: Date;
  providerId: string;
  data: staffGetEventCardsQuery | undefined;
  error: ApolloError | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<staffGetEventCardsQuery>;
}

export const MainFocusArea = ({
  currentTime,
  data,
  error,
  isLoading,
  mutate,
  providerId,
}: MainFocusAreaProps) => {
  const styles = useStyles();
  const rootStore = useRootStore();
  const clCanAcceptPrescriptionTasks = useFeatureFlag(CL_CAN_ACCEPT_PRESCRIPTION_TASKS);

  // This listens for the eventChanged action from the MobX
  // store and triggers a re-fetch of the event data.
  // It's not clear why the `true` parameter is required (it
  // is supposed to be true by default), but the middleware
  // doesn't trigger without it.
  useEffect(() => {
    if (!rootStore || !data || isLoading || error || !isStateTreeNode(rootStore)) return () => {};

    return addMiddleware(
      rootStore,
      (action, next) => {
        if (action.name === 'eventChanged') {
          setImmediate(() => mutate());
        }

        return next(action);
      },
      true,
    );
  }, [rootStore, mutate, data, isLoading, error]);

  if (!data || isLoading) {
    // Loading state
    return <div className={`${styles.container} empty centered`}>Loading...</div>;
  }

  if (error) {
    return (
      <div className={styles.container}>
        Received error attempting to fetch next event: {error.message ?? '[No error message]'}
      </div>
    );
  }

  // Event card logic
  const { nextEvent } = data.staff_myDayTodayEventCards ?? {};
  const teamRole = (rootStore.auth.user as Provider)?.teamRole as ProviderRole;
  const nextEventStartingSoon =
    nextEvent && subMinutes(parseISO(nextEvent.start), 5) <= currentTime;

  const prescriptionTask = data.staff_prescriptionTaskForProvider;
  const showPrescriptionTask =
    prescriptionTask && !nextEventStartingSoon && clCanAcceptPrescriptionTasks;

  if (nextEvent && shouldRenderEventCard(nextEvent, teamRole) && !showPrescriptionTask) {
    return renderEventCard(nextEvent, currentTime, styles, mutate);
  }

  // Unsigned visit card logic
  if (
    data.staff_getProviderUnsignedVisits.length &&
    data.staff_getProviderUnsignedVisits.length > 0
  ) {
    return (
      <UnsignedVisitsCard
        providerId={providerId}
        unsignedVisits={data.staff_getProviderUnsignedVisits}
      />
    );
  }

  // Prescription task card logic
  if (showPrescriptionTask) {
    return (
      <PrescriptionTaskCard
        providerId={providerId}
        prescriptionTask={prescriptionTask}
        mutate={mutate}
      />
    );
  }

  if (!data.staff_myDayTodayEventCards.emptyCardClassification) {
    return (
      <div className={styles.container}>Unreachable: No matching empty card classification:</div>
    );
  }

  // Empty focus card logic
  return (
    <div className={`${styles.container} empty`}>
      <EmptyFocusCard type={data.staff_myDayTodayEventCards.emptyCardClassification} />
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    boxSizing: 'border-box',
    display: 'flex',
    width: '675px',
    minHeight: '485px',
    padding: '24px 36px',
    flexDirection: 'column',
    justifyContent: 'space-around',
    gap: '8px',
    border: '2px solid',
    borderColor: BoulderColors.Blue4,
    background: BoulderColors.White,
    boxShadow: '4px 4px 25px 0px rgba(0, 0, 0, 0.05)',
    '&.empty': {
      borderWidth: 1,
      borderColor: BoulderColors.Gray2,
      boxShadow: 'none',
    },
    '&.imminent': {
      backgroundColor: BoulderColors.Blue1,
    },
    '&.late': {
      backgroundColor: Colors.NewLightRed,
      borderColor: colors.errorLight,
    },
    '&.alignTop': {
      justifyContent: 'flex-start',
    },
    '&.centered': {
      justifyContent: 'center',
      alignItems: 'center',
      alignSelf: 'stretch',
    },
  },
  header: {
    color: Colors.BoulderBlue,
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '145%',
    letterSpacing: '0.201px',
  },
});

function renderEventCard(
  nextEvent: EventCard,
  currentTime: Date,
  styles: ClassNameMap,
  onScheduleRefresh: () => void,
) {
  const containerClasses = [styles.container];
  const eventClassification = nextEvent.classification;
  let EventCardComponent: React.ReactNode;
  const nextEventStart = parseISO(nextEvent.start);
  let eventTiming = getScheduledEventTiming(currentTime, nextEventStart);

  switch (eventClassification) {
    case EventCardClassification.ScheduledWithPatient:
      EventCardComponent = renderScheduledEventWithPatient(nextEvent, currentTime);
      containerClasses.push('alignTop');
      break;
    case EventCardClassification.ScheduledWithoutPatient:
      EventCardComponent = renderScheduledEventWithoutPatient(nextEvent, currentTime);
      containerClasses.push('alignTop');
      break;
    case EventCardClassification.DropIn:
      EventCardComponent = renderDropInEvent(nextEvent, currentTime, onScheduleRefresh);
      eventTiming = EventTiming.Imminent;
      containerClasses.push('alignTop');
      break;
    default:
      EventCardComponent = renderUnknownEventClassification(eventClassification);
  }

  containerClasses.push(eventTiming);

  return (
    <div className={containerClasses.join(' ')}>
      <div className={styles.header}>
        {eventTiming === EventTiming.Soon || eventTiming === EventTiming.Future
          ? 'Coming Soon:'
          : 'Now:'}
      </div>
      {EventCardComponent}
    </div>
  );
}

function renderScheduledEventWithPatient(data: EventCard, currentTime: Date) {
  const event = {
    id: data.id,
    start: parseISO(data.start),
    duration: data.duration,
    title: data.title,
    type: data.type,
    subType: data.subType,
  };

  return (
    <div>
      <ScheduledEventCard
        currentTime={currentTime}
        event={event}
        patient={transformPatient(data.patient)}
        visitType={data.subType}
        visitReason={data.visitReason || undefined}
      />
    </div>
  );
}

function renderScheduledEventWithoutPatient(data: EventCard, currentTime: Date) {
  const event = {
    start: parseISO(data.start),
    duration: data.duration,
    title: data.title,
    subType: data.subType,
  };

  return (
    <div>
      <NonPatientEventCard currentTime={currentTime} event={event} />
    </div>
  );
}

function renderDropInEvent(data: EventCard, currentTime: Date, onScheduleRefresh: () => void) {
  return (
    <div>
      <PopInVisitCard
        currentTime={currentTime}
        expectedDuration={data.duration}
        patient={transformPatient(data.patient)}
        requestId={data.requestId || ''}
        scheduledStartTime={parseISO(data.start)}
        visitType={data.subType}
        visitReason={data.visitReason || undefined}
        reasonDetails={(data.reasonDetails || undefined) as Record<string, any> | undefined}
        hasBeenSufficientlyNotified={!!data.hasBeenSufficientlyNotified}
        prepResponsibility={data.prepResponsibility || undefined}
        onScheduleRefresh={onScheduleRefresh}
      />
    </div>
  );
}

function renderUnknownEventClassification(eventClassification: string) {
  return <div>Unknown event type: {eventClassification}</div>;
}
